import { fetchAuthSession } from "aws-amplify/auth";
import {
  SessionBroadcastMessage,
  SessionConfig,
  SessionStatus,
} from "data/@types/SessionManagement";
import { getCurrentUser, signOut } from "data/services/amplify/AmplifyService";
import { setAxiosAuthorizationHeader } from "data/services/api/axios-config";
import { useAuthStore } from "data/store/Auth";
import { getEnvConfig } from "data/store/EnvConfig";
import { isUserIdle } from "ui/components/session/IdleDetector";

class TokenRefreshManager {
  private checkTimeout: NodeJS.Timeout | null = null;
  private countdownTimerInterval: NodeJS.Timeout | null = null;
  private logoutTimeout: NodeJS.Timeout | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private originalTabTitle: string = "";
  private currentStatus: SessionStatus = SessionStatus.ACTIVE;
  private tokenExpirationTime: number | null = null;
  private isModalVisible: boolean = false;
  private hasAutoRefreshed: boolean = false;
  private isInitialized: boolean = false;

  private config: SessionConfig = {
    warningThreshold: 5 * 60,
    enableMultiTabSync: true,
  };

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    await this.loadConfig();

    if (this.config.enableMultiTabSync) {
      this.setupBroadcastChannel();
    }

    this.startTokenCheck();
    this.originalTabTitle = document.title;
    this.isInitialized = true;
  }

  private async loadConfig() {
    try {
      const envConfig = await getEnvConfig();

      this.config = {
        warningThreshold: (Number(envConfig.tokenWarningThreshold) || 5) * 60,
        enableMultiTabSync: envConfig.enableMultiTabSync !== "false",
      };
    } catch (error) {
      console.error("Error loading session config, using defaults:", error);
    }
  }

  private setupBroadcastChannel() {
    if ("BroadcastChannel" in window) {
      this.broadcastChannel = new BroadcastChannel("session-sync");
      this.broadcastChannel.onmessage = (event) => {
        const message = event.data as SessionBroadcastMessage;

        switch (message.type) {
          case "TOKEN_REFRESHED":
            this.syncTokenFromOtherTab();
            break;
          case "SESSION_EXTENDED":
            this.closeInactivityModal();
            this.stopCountdownTimer();
            this.resetTabTitle();
            break;
          case "LOGOUT":
            this.handleLogout();
            break;
        }
      };
    }
  }

  private async startTokenCheck() {
    await this.checkTokenExpiration();
  }

  private scheduleNextTokenCheck(timeUntilExpiry: number) {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
      this.checkTimeout = null;
    }

    if (timeUntilExpiry > this.config.warningThreshold) {
      const timeUntilWarning = timeUntilExpiry - this.config.warningThreshold;
      const delayMs = timeUntilWarning * 1000;

      this.checkTimeout = setTimeout(() => {
        this.checkTokenExpiration();
      }, delayMs);
    }
  }

  private async checkTokenExpiration() {
    try {
      if (this.isModalVisible && this.tokenExpirationTime) {
        await this.checkCachedTokenExpiration();
        return;
      }

      await this.checkFreshTokenExpiration();
    } catch (error) {
      console.error("Error checking token expiration:", error);
    }
  }

  private async checkCachedTokenExpiration() {
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = this.tokenExpirationTime! - currentTime;

    if (timeUntilExpiry <= 0) {
      await this.handleExpiredToken();
    } else {
      await this.handleTokenWarningPeriod(timeUntilExpiry);
    }
  }

  private async checkFreshTokenExpiration() {
    const tokenInfo = await this.getTokenExpirationInfo();
    if (!tokenInfo) return;

    const { timeUntilExpiry } = tokenInfo;
    await this.handleTokenState(timeUntilExpiry);
  }

  private async handleTokenState(timeUntilExpiry: number) {
    if (timeUntilExpiry > this.config.warningThreshold) {
      this.handleHealthyToken();
      this.scheduleNextTokenCheck(timeUntilExpiry);
    } else if (timeUntilExpiry <= 0) {
      await this.handleExpiredToken();
    } else {
      await this.handleTokenWarningPeriod(timeUntilExpiry);
    }
  }

  private async getTokenExpirationInfo(): Promise<{
    timeUntilExpiry: number;
  } | null> {
    const session = await fetchAuthSession();
    const expirationTime = session.tokens?.accessToken?.payload.exp as
      | number
      | undefined;

    if (!expirationTime) {
      return null;
    }

    this.tokenExpirationTime = expirationTime;

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expirationTime - currentTime;

    return { timeUntilExpiry };
  }

  private handleHealthyToken() {
    if (this.currentStatus !== SessionStatus.ACTIVE) {
      this.currentStatus = SessionStatus.ACTIVE;
      this.hasAutoRefreshed = false;
      this.stopCountdownTimer();
      this.resetTabTitle();
    }
  }

  private async handleExpiredToken() {
    this.currentStatus = SessionStatus.EXPIRED;
    this.updateTabTitle(0);
    this.showInactivityModal(0);
    await this.logout();
  }

  private async handleTokenWarningPeriod(timeUntilExpiry: number) {
    this.currentStatus = SessionStatus.WARNING;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (isUserIdle()) {
      this.handleInactiveUser(timeUntilExpiry);
    } else {
      await this.handleActiveUser();
    }
  }

  private handleInactiveUser(timeUntilExpiry: number) {
    this.startCountdownTimer();
    this.showInactivityModal(timeUntilExpiry);
  }

  private async handleActiveUser() {
    if (!this.isModalVisible && !this.hasAutoRefreshed) {
      this.hasAutoRefreshed = true;
      await this.refreshToken();
    }
  }

  private async refreshToken() {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });

      if (session.tokens?.idToken) {
        const user = await getCurrentUser();
        if (user) {
          this.updateUserAuthentication(user);
          this.broadcastMessage({ type: "TOKEN_REFRESHED" });
          this.cancelPendingLogout();
          this.resetSessionState();
          await this.rescheduleTokenCheck();
        }
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  }

  private updateUserAuthentication(user: any) {
    setAxiosAuthorizationHeader(`Bearer ${user.identityToken}`);
    useAuthStore.getState().setUser(user);
  }

  private cancelPendingLogout() {
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = null;
    }
  }

  private resetSessionState() {
    this.currentStatus = SessionStatus.ACTIVE;
    this.isModalVisible = false;
    this.hasAutoRefreshed = false;
    this.stopCountdownTimer();
    this.resetTabTitle();
  }

  private async rescheduleTokenCheck() {
    const tokenInfo = await this.getTokenExpirationInfo();
    if (tokenInfo) {
      this.scheduleNextTokenCheck(tokenInfo.timeUntilExpiry);
    }
  }

  private async syncTokenFromOtherTab() {
    try {
      const user = await getCurrentUser();
      if (user) {
        this.updateUserAuthentication(user);
        this.cancelPendingLogout();
        this.resetSessionStateWithModal();
        await this.rescheduleTokenCheck();
      }
    } catch (error) {
      console.error("Error syncing token from other tab:", error);
    }
  }

  private resetSessionStateWithModal() {
    this.isModalVisible = false;
    this.closeInactivityModal();
    this.stopCountdownTimer();
    this.resetTabTitle();
    this.currentStatus = SessionStatus.ACTIVE;
  }

  private updateTabTitle(secondsRemaining: number) {
    const totalSeconds = Math.floor(secondsRemaining);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const timeString = `${minutes}:${seconds.toString().padStart(2, "0")}`;

    if (secondsRemaining > 0) {
      document.title = `${timeString} - ${this.originalTabTitle}`;
    } else {
      document.title = `${this.originalTabTitle} - Session Expired`;
    }
  }

  private resetTabTitle() {
    document.title = this.originalTabTitle;
  }

  private startCountdownTimer() {
    if (this.countdownTimerInterval) {
      return;
    }

    this.updateCountdownDisplays();

    this.countdownTimerInterval = setInterval(() => {
      this.updateCountdownDisplays();
    }, 1000);
  }

  private stopCountdownTimer() {
    if (this.countdownTimerInterval) {
      clearInterval(this.countdownTimerInterval);
      this.countdownTimerInterval = null;
    }
  }

  private updateCountdownDisplays() {
    if (!this.tokenExpirationTime) {
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = Math.max(0, this.tokenExpirationTime - currentTime);

    this.updateTabTitle(timeUntilExpiry);

    window.dispatchEvent(
      new CustomEvent("update-countdown", {
        detail: { secondsRemaining: timeUntilExpiry },
      }),
    );
  }

  private showInactivityModal(secondsRemaining: number) {
    if (!this.isModalVisible) {
      this.isModalVisible = true;
      window.dispatchEvent(
        new CustomEvent("show-inactivity-modal", {
          detail: { secondsRemaining },
        }),
      );
    }
  }

  private closeInactivityModal() {
    this.isModalVisible = false;
    window.dispatchEvent(new CustomEvent("close-inactivity-modal"));
  }

  private broadcastMessage(message: SessionBroadcastMessage) {
    if (this.broadcastChannel && this.config.enableMultiTabSync) {
      this.broadcastChannel.postMessage({
        ...message,
        timestamp: Date.now(),
      });
    }
  }

  async extendSession() {
    try {
      await this.refreshToken();
      this.currentStatus = SessionStatus.ACTIVE;
      this.broadcastMessage({ type: "SESSION_EXTENDED" });
      this.stopCountdownTimer();
      this.resetTabTitle();
      this.closeInactivityModal();
    } catch (error) {
      console.error("Error extending session:", error);
      throw error;
    }
  }

  private async handleLogout() {
    this.cleanup();
    await signOut();
  }

  async logout() {
    this.broadcastMessage({ type: "LOGOUT" });
    this.cleanup();
    await signOut();
  }

  cleanup() {
    this.clearAllTimers();
    this.closeBroadcastChannel();
    this.resetState();
  }

  private clearAllTimers() {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
      this.checkTimeout = null;
    }

    this.stopCountdownTimer();
    this.cancelPendingLogout();
  }

  private closeBroadcastChannel() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  private resetState() {
    this.resetTabTitle();
    this.currentStatus = SessionStatus.ACTIVE;
    this.tokenExpirationTime = null;
    this.isModalVisible = false;
    this.hasAutoRefreshed = false;
    this.isInitialized = false;
  }

  getStatus(): SessionStatus {
    return this.currentStatus;
  }
}

export const tokenRefreshManager = new TokenRefreshManager();
